"use client";

import { cn } from "@/lib/utils";
import React, { useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import { Button } from "@/components/ui/button";
import { useWebViewAuthModal } from "@/hooks/client/useWebViewAuthModal";
import StoryCarousel from "./StoryCarousel";

const logoItems: Array<{
  logo: string;
  url: string;
  logoSrc?: string;
}> = [
  {
    logo: "ny-times.png",
    url: "https://www.nytimes.com/2025/08/19/travel/ai-travel-tools-test.html",
  },
  {
    logo: "nerdwallet.png",
    url: "https://www.nerdwallet.com/travel/learn/gondola-review",
  },
  {
    logo: "frequent-miler.png",
    url: "https://frequentmiler.com/gondola/",
  },
  {
    logo: "afar.png",
    url: "https://www.afar.com/magazine/we-tested-ai-travel-planning-apps-here-are-the-3-that-actually-worked",
  },
  {
    logo: "upgraded-points.png",
    url: "https://upgradedpoints.com/news/gondola-hotel-booking-tool/",
  },
  // {
  //   logo: "business-traveler.png",
  //   url: "",
  // },
  {
    logo: "view-wing.png",
    url: "https://viewfromthewing.com/gondolas-ai-unlocks-the-best-hotel-points-deals-and-pays-you-cash-back-when-you-book-direct",
  },
  {
    logo: "cbs-news.png",
    url: "https://www.cbsnews.com/news/ai-travel-planning-tools/",
  },
  {
    logo: "today-show.png",
    url: "https://www.today.com/video/new-ways-to-plan-a-vacation-social-media-tools-ai-home-swap-260186181955",
  },
  {
    logo: "tpg.png",
    url: "https://thepointsguy.com/loyalty-programs/monthly-valuations/",
  },
];

interface BannerSliderProps {
  // "compact" (default) is the original press-logo strip: a small caption
  // line + marquee, used inside AboutHero/SuitesHero's fixed-height
  // (`h-[calc(100dvh-64px)]`) hero boxes. "full" adds the story carousel,
  // stat line, and CTA — homepage-only, since that much extra height would
  // blow out the other two pages' fixed-height layouts.
  variant?: "compact" | "full";
}

const BannerSlider: React.FC<BannerSliderProps> = ({ variant = "compact" }) => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const { handleClick, AuthModals } = useWebViewAuthModal();
  const isFull = variant === "full";

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);
  }, []);

  return (
    <>
      <div className="mx-auto flex w-full max-w-[1240px] flex-col items-center justify-start gap-6 pb-6">
        {isFull ? (
          <h2 className="w-full text-balance text-center font-figtree text-3xl font-semibold leading-9 text-foreground lg:text-5xl lg:leading-none">
            The go-to for hundreds of thousands of savvy travelers
          </h2>
        ) : (
          <div className="w-full px-6 text-center font-figtree text-base font-medium leading-5 text-muted-foreground">
            The go-to for hundreds of thousands of savvy travelers
          </div>
        )}

        {/* Static single row on wide screens; a scrolling marquee only
            kicks in where a static row wouldn't fit all the logos.
            9 logos at their real aspect ratios need ~2957px to render at a
            100px-tall row with no shrinking — far more than any real
            viewport, so flex-shrink always renormalizes every logo down to
            fit whatever width this row actually has. Bumping the `<img>`
            height class alone is a no-op: the row's available width is what
            determines the rendered size, not the class. Widening the row
            past the page's usual 1240px content column (matching the "make
            the logos a little bigger, taking up more width" ask) is what
            actually grows them — capped at 92vw so it can never force a
            horizontal scrollbar on narrower `xl` widths. */}
        <div className="hidden w-full items-center justify-items-center gap-4 xl:flex xl:w-[min(1600px,92vw)]">
          {logoItems.map((logo, index) => (
            <a
              className="flex items-center justify-center"
              key={index}
              href={logo.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={
                  logo.logoSrc ??
                  `/assets/landing-page/read-about-us/${logo.logo}`
                }
                alt={logo.logo.replace(/\..+$/, "")}
                className="h-[70px] w-auto object-contain opacity-70 md:h-[100px]"
                width={450}
                height={138}
                loading="lazy"
              />
            </a>
          ))}
        </div>

        <div className="block w-full xl:hidden">
          <Marquee
            className="w-full"
            direction="right"
            play={!prefersReducedMotion}
          >
            {logoItems.map((logo, index) => (
              <a
                className={cn("mx-3 flex items-center justify-center")}
                key={index}
                href={logo.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={
                    logo.logoSrc ??
                    `/assets/landing-page/read-about-us/${logo.logo}`
                  }
                  alt={logo.logo.replace(/\..+$/, "")}
                  className="h-[70px] w-auto object-contain opacity-70 md:h-[100px]"
                  width={450}
                  height={138}
                  loading="lazy"
                />
              </a>
            ))}
          </Marquee>
        </div>

        {isFull && (
          <>
            <StoryCarousel />

            <Button
              onClick={handleClick}
              variant="orange-gradient"
              className="font-figtree leading-6 text-pure-white"
            >
              See why travelers are talking
            </Button>
          </>
        )}
      </div>
      {isFull && <AuthModals />}
    </>
  );
};

export default BannerSlider;
